import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react'
import config from '@/config/config.env'
import { supabase } from '@/services/supabase'
import { nanoid } from 'nanoid'
import { useDropzone } from 'react-dropzone'
// import { getWorkerFile } from '../utils/helpers'
import { toast } from 'sonner'
import { getWorkerFile } from '@/utils/helpers/workers'

declare global {
  interface Window {
    Worker: {
      onmessage: (e: any) => void
      onerror: (e: any) => void
      terminate: () => void
      postMessage: (e: any) => void
    }
  }
}

const FileUploadContext = createContext<{
  isLoading: boolean
  data: any
  progress: number
  error: any
  sendPostMessage: (obj: any) => void
  handleOpen: () => void
  handleClose: () => void
  handleMinimize: () => void
  modal: {
    open: boolean
    minimize: boolean
  }
  selectedFiles: Array<{ file: File; id: string }>
  setProgress: (progress: number) => void
  setSelectedFiles: any
  uploadFiles: () => Promise<void>
  dropzone: {
    getRootProps: () => any
    getInputProps: () => any
    isDragActive: boolean
  }
}>({} as any)
const ResDBContext = ({ children }: { children: React.ReactNode }) => {
  const [worker, setWorker] = useState<Window>(null as any)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [selectedFiles, setSelectedFiles] = useState([])

  useEffect(() => {
    setWorker(
      // @ts-ignore this is worker for worker
      new Worker(getWorkerFile('/app/webworkers/res-db-file-upload.js'))
    )
  }, [])

  //   OPEN AND MINIMIZE
  const [modal, setModal] = useState({
    open: false,
    minimize: false,
  })
  const onDrop = (acceptedFiles: any) => {
    if (acceptedFiles.length <= 2000) {
      const documents = acceptedFiles.map((file: any) => ({
        file,
        id: nanoid(6),
      }))
      setSelectedFiles(documents)
      setModal((modal) => ({ ...modal, open: true }))
    } else {
      toast.error('Files should be less than or equal to 2000')
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        [],
    },
    multiple: true,
  })

  useEffect(() => {
    // eslint-disable-next-line
    if (!!worker) {
      worker.onmessage = (e) => {
        // eslint-disable-next-line
        if (!!e.data.isCompleted) {
          if ('error' in e.data) {
            setError(e.data.error)
          } else {
            setProgress(100)
            setData(e.data.documents)
            if (e.data.isQueued) {
              if (modal.minimize) {
                toast.success("You'll be notified once it's done")
              }
            } else {
              if (modal.minimize)
                toast.success("Your document's have been successfully uploaded")
            }
            setIsLoading(false)
          }
        } else if (e.data.progress) {
          setProgress(e.data.progress)
        }
      }

      worker.onerror = (e: any) => {
        setIsLoading(false)
        // #@ts-ignore t
        setError(e.message)
        handleClose()
        // toast.error("Something went wrong please try again later");
        setProgress(0)
        setData(null)
      }

      // Clean up
      return () => {
        // @ts-ignore this is worker terminate
        worker.terminate()
      }
    }
    // eslint-disable-next-line
  }, [worker])

  useEffect(() => {
    const handleBeforeUnload = (event: any) => {
      if (isLoading) {
        // Cancel the event as stated by the standard.
        event.preventDefault()
        // Chrome requires returnValue to be set.
        const message =
          'Resumes are uploading. If you leave now, the upload will be cancelled.'
        event.returnValue = message
        return message
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isLoading])

  // Function to post messages to the worker
  const postMessage = useCallback(
    (message: any) => {
      if (worker) {
        setIsLoading(true)
        // @ts-ignore this is worker postmessage
        worker.postMessage(message)
      }
    },
    [worker]
  )
  const handleOpen = () => {
    setModal({ minimize: false, open: true })
  }
  function handleClose() {
    setProgress(0)
    setData(null)
    setError(null)
    setModal({ minimize: false, open: false })
    setSelectedFiles([])
  }
  const handleMinimize = () => {
    setModal((prevData) => {
      return { ...prevData, minimize: !prevData.minimize }
    })
  }

  const sendPostMessage = (obj: any) => {
    postMessage(obj)
  }

  const handleSelectedFiles = () => {
    setSelectedFiles([])
  }

  const uploadFiles = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const authToken = session?.access_token
    const documents = [...selectedFiles]

    sendPostMessage({
      URL: config.urls.baseUrl,
      documents,
      authToken,
    })
    handleSelectedFiles()
  }

  // Handle Minimize and handle close

  return (
    <FileUploadContext.Provider
      value={{
        isLoading,
        data,
        progress,
        error,
        sendPostMessage,
        handleOpen,
        handleClose,
        handleMinimize,
        modal,
        selectedFiles,
        setProgress,
        setSelectedFiles,
        uploadFiles,
        dropzone: { getRootProps, getInputProps, isDragActive },
      }}
    >
      {children}
    </FileUploadContext.Provider>
  )
}

export default ResDBContext

export const useFileUploadContext = () => {
  return useContext(FileUploadContext)
}
